import type { ReactNode } from 'react'

import { cn, useDocumentTitle } from '@/shared/lib'

type PageHeaderProps = {
  /** Mono, uppercase, +14% tracking — the section a page belongs to. */
  eyebrow?: ReactNode
  title: ReactNode
  description?: ReactNode
  /** Primary actions for the page — buttons, filters, a create control. */
  actions?: ReactNode
  className?: string
}

/**
 * The page furniture every screen repeats: mono eyebrow, h1, one line of
 * description, actions on the right. The title is fluid between 22 and 24.
 *
 * It also names the document. Every screen in the panel used to be «Admin» —
 * five tabs looked the same, history was a column of one word, and a screen
 * reader announced the same string after every navigation. Doing it here rather
 * than in each page means a screen cannot be added without a title, which is
 * exactly how the old one went unnoticed.
 */
export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
  className,
}: PageHeaderProps) {
  // Only when the heading is a plain string. A composed one — an id with a
  // badge beside it — belongs to the record archetype, which names itself.
  useDocumentTitle(typeof title === 'string' ? title : undefined)

  return (
    <header
      data-slot="page-header"
      className={cn('flex flex-wrap items-end gap-4', className)}
    >
      <div className="min-w-65 flex-1">
        {eyebrow !== undefined && (
          <div className="mb-2.5 font-mono text-label tracking-eyebrow text-muted-foreground uppercase">
            {eyebrow}
          </div>
        )}
        <h1 className="text-[clamp(22px,2.4vw,24px)] leading-heading font-semibold tracking-h1">
          {title}
        </h1>
        {description !== undefined && (
          <p className="mt-2 max-w-155 text-body leading-text text-fg-secondary text-pretty">
            {description}
          </p>
        )}
      </div>
      {actions !== undefined && <div className="flex items-center gap-2">{actions}</div>}
    </header>
  )
}
